
import { SimulationConfig, Ball, EscapeRing, Vector2D, ShapeType } from './types';
import { drawAdvancedBall } from './BallDrawer';
import { ShapeUtils } from './ShapeUtils';
import { MorphData } from './physicsUtils';

// Optimized Hex Parser avoiding Regex
const parseHex = (c: string) => {
    if (c.startsWith('#')) c = c.slice(1);
    if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2];
    const v = parseInt(c, 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};

const lerpColor = (a: string, b: string, t: number) => {
  const c1 = parseHex(a);
  const c2 = parseHex(b);
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
  const bl = Math.round(c1[2] + (c2[2] - c1[2]) * t);
  return `rgb(${r},${g},${bl})`;
};

export const drawBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: SimulationConfig
) => {
  if (config.darkMode) {
    ctx.fillStyle = (config.rgbLighting && config.useContainerRGB) ? '#050505' : (config.outerColor === '#f5f5dc' ? '#0a0a0a' : config.outerColor);
  } else {
    ctx.fillStyle = config.outerColor;
  }
  ctx.fillRect(0, 0, width, height);
};

export const drawContainer = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  config: SimulationConfig,
  globalHue: number,
  dynamicRadius: number,
  morph?: MorphData
) => {
  const isEscape = config.mode === 'ESCAPE';
  const useRGB = config.rgbLighting && config.useContainerRGB;
  const radius = dynamicRadius || config.containerRadius;
  const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
  const targetShape = morph ? morph.to : shape;
  const t = morph ? morph.factor : 0;
  const rotRad = (config.containerRotation || 0) * Math.PI / 180;
  
  if (!isEscape || config.escapeShowOuterBorders) {
    for (let i = 0; i < config.numOuterBorders; i++) {
      ctx.beginPath(); 
      if (shape === 'CIRCLE' && targetShape === 'CIRCLE') {
          ctx.arc(centerX, centerY, radius + i * 8, 0, Math.PI * 2);
      } else {
          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate(rotRad);
          ShapeUtils.traceInterpolatedShape(ctx, {x: 0, y: 0}, shape, targetShape, t, radius + i * 8, morph?.baseRadius);
          ctx.restore();
      }
      ctx.strokeStyle = useRGB ? `hsl(${(globalHue + i * 20) % 360}, 100%, 50%)` : config.borderColor;
      ctx.lineWidth = config.containerLineWidth; 
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  }

  if (!isEscape) {
    ctx.beginPath();
    if (shape === 'CIRCLE' && targetShape === 'CIRCLE') {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    } else {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotRad);
        ShapeUtils.traceInterpolatedShape(ctx, {x: 0, y: 0}, shape, targetShape, t, radius, morph?.baseRadius);
        ctx.restore();
    }
    if (config.darkMode) {
      ctx.fillStyle = useRGB ? '#0a0a0a' : (config.innerColor === '#ffffff' ? '#111' : config.innerColor);
    } else {
      ctx.fillStyle = config.innerColor;
    }
    ctx.fill();
  }
};

export const drawEscapeRings = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  rings: EscapeRing[],
  config: SimulationConfig,
  globalHue: number,
  morph?: MorphData
) => {
  const visibleLimit = Math.min(rings.length, config.escapeMaxVisibleRings);
  const useRGB = config.rgbLighting && config.useContainerRGB;
  const totalRings = config.escapeRingCount;
  const oppositeCount = config.escapeOppositeGapCount || 0;
  
  // Resolve shape
  const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
  const targetShape = morph ? morph.to : shape;
  const tMorph = morph ? morph.factor : 0;
  const rotRad = (config.containerRotation || 0) * Math.PI / 180;
  
  const isCircle = shape === 'CIRCLE' && targetShape === 'CIRCLE';
  const isLinear = ShapeUtils.isLinearShape(shape);
  const useScaling = isLinear; 

  // PERFORMANCE STRATEGY:
  // For Non-Linear shapes (Curves), use LUT for speed.
  // For Linear shapes (Squares), use EXACT math to ensure perfectly straight lines and parallel gaps.
  const USE_LUT = !isLinear && !isCircle;
  
  const LUT_RES = 720;
  let unitLUT: Float32Array | null = null;
  if (USE_LUT) {
      unitLUT = ShapeUtils.generateUnitRadiusLUT(shape, targetShape, tMorph, LUT_RES);
  }

  const TwoPI = Math.PI * 2;
  
  // Side Guard State
  let prevNormalGapPoints: { start: {x:number, y:number}, end: {x:number, y:number} } | null = null;
  let prevOppositeGapPoints: { start: {x:number, y:number}, end: {x:number, y:number} } | null = null;
  const baseRadius = config.containerRadius;

  for (let idx = 0; idx < visibleLimit; idx++) {
      const ring = rings[idx];
      if (ring.radius < 2) continue;

      const currentShift = Math.max(0, totalRings - rings.length);
      const originalIndex = currentShift + idx;
      const isOpposite = originalIndex < oppositeCount;
      const effectiveGapSize = isOpposite ? (config.escapeOppositeGapSize || 30) : config.escapeGapSize;

      // Color Logic
      let ringColor = '';
      if (useRGB || config.escapeColorMode === 'RAINBOW') {
        const ringHue = (globalHue + idx * 8) % 360;
        ringColor = `hsl(${ringHue}, 100%, 60%)`;
      } else if (config.escapeColorMode === 'UNIFORM') {
        ringColor = config.escapeUniformColor;
      } else if (config.escapeColorMode === 'INDIVIDUAL') {
        ringColor = `hsl(${ring.hue}, 100%, 50%)`;
      } else if (config.escapeColorMode === 'ALTERNATING') {
        ringColor = idx % 2 === 0 ? config.escapeColorA : config.escapeColorB;
      } else if (config.escapeColorMode === 'GRADIENT') {
        ringColor = lerpColor(config.escapeColorA, config.escapeColorB, idx / visibleLimit);
      } else {
        ringColor = config.borderColor;
      }

      const alpha = config.escapeStaticMode ? (0.85 * config.escapeRingAlphaBase) : Math.max(0.08, (config.escapeRingAlphaBase - Math.pow(idx / visibleLimit, config.escapeRingAlphaFade)));

      ctx.beginPath();
      ctx.strokeStyle = ringColor;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = config.escapeRingThickness * (config.escapeStaticMode ? 1 : alpha); 
      ctx.lineJoin = 'round'; 

      // Gap Calculation
      let halfGapRad = (effectiveGapSize * Math.PI / 180) / 2;
      
      if (config.escapeGapAlignment === 'FIXED_SIZE') {
          const baseArcLength = (effectiveGapSize * Math.PI / 180) * config.containerRadius;
          const fixedGapAngle = baseArcLength / ring.radius;
          halfGapRad = fixedGapAngle / 2;
      }

      const gapStart = ring.angle - halfGapRad;
      const gapEnd = ring.angle + halfGapRad;
      
      // -- DRAW RING PATH --
      if (isCircle) {
          ctx.arc(centerX, centerY, ring.radius, gapEnd, gapStart); 
      } else {
          // If NOT Circle, we draw segment by segment
          const startTheta = gapEnd;
          const totalArc = TwoPI - (2 * halfGapRad);
          
          // OPTIMIZED ADAPTIVE RESOLUTION
          // Calculate approx visual arc length in pixels
          const arcLength = ring.radius * totalArc;
          // Target ~12 pixels per segment. Smoother for larger rings, sparse for small.
          let drawSteps = Math.ceil(arcLength / 12);
          // Clamp to reasonable limits to prevent lag on huge rings or blockiness on small
          drawSteps = Math.max(16, Math.min(drawSteps, 200));
          
          const stepAngle = totalArc / drawSteps;
          
          for (let i = 0; i <= drawSteps; i++) {
              const theta = startTheta + i * stepAngle;
              let shapeAngle = (theta - rotRad) % TwoPI;
              if (shapeAngle < 0) shapeAngle += TwoPI;
              
              let r = 0;
              
              if (USE_LUT && unitLUT) {
                  const lutIndex = Math.floor((shapeAngle / TwoPI) * LUT_RES) % LUT_RES;
                  const unitR = unitLUT[lutIndex];
                  if (useScaling) {
                      r = unitR * ring.radius;
                  } else {
                      r = (unitR * baseRadius) + (ring.radius - baseRadius);
                  }
              } else {
                  // EXACT CALCULATION with INTERPOLATION
                  r = ShapeUtils.getInterpolatedRadiusAtAngle(
                      shapeAngle, 
                      shape, 
                      targetShape, 
                      tMorph, 
                      ring.radius, 
                      baseRadius
                  );
              }
              
              const px = centerX + Math.cos(theta) * r;
              const py = centerY + Math.sin(theta) * r;
              
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
          }
      }
      ctx.stroke();

      // -- DRAW SIDE GUARDS --
      if (config.escapeSideGuards) {
          // Calculate start/end points using exact same logic as above
          let rStart = ring.radius;
          let rEnd = ring.radius;

          if (!isCircle) {
              const getR = (t: number) => {
                  let shapeAngle = (t - rotRad) % TwoPI;
                  if (shapeAngle < 0) shapeAngle += TwoPI;
                  
                  if (USE_LUT && unitLUT) {
                      const idx = Math.floor((shapeAngle / TwoPI) * LUT_RES) % LUT_RES;
                      const unitR = unitLUT[idx];
                      if (useScaling) return unitR * ring.radius;
                      return (unitR * baseRadius) + (ring.radius - baseRadius);
                  } else {
                      // Exact with Interpolation
                      return ShapeUtils.getInterpolatedRadiusAtAngle(
                          shapeAngle, 
                          shape, 
                          targetShape, 
                          tMorph, 
                          ring.radius, 
                          baseRadius
                      );
                  }
              };
              rStart = getR(gapStart);
              rEnd = getR(gapEnd);
          }

          const p1x = centerX + Math.cos(gapStart) * rStart;
          const p1y = centerY + Math.sin(gapStart) * rStart;
          const p2x = centerX + Math.cos(gapEnd) * rEnd;
          const p2y = centerY + Math.sin(gapEnd) * rEnd;

          // Determine which previous points to use based on ring type
          const prevPoints = isOpposite ? prevOppositeGapPoints : prevNormalGapPoints;

          if (prevPoints) {
              ctx.beginPath();
              // Use white or high contrast color
              ctx.strokeStyle = config.darkMode ? '#ffffff' : '#000000'; 
              ctx.lineWidth = Math.max(3, config.escapeRingThickness * 0.8); 
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              // Ensure visibility even if ring is faded
              ctx.globalAlpha = Math.min(1.0, alpha + 0.6); 
              
              ctx.moveTo(prevPoints.start.x, prevPoints.start.y);
              ctx.lineTo(p1x, p1y);
              ctx.moveTo(prevPoints.end.x, prevPoints.end.y);
              ctx.lineTo(p2x, p2y);
              ctx.stroke();
          }

          // Update the correct state
          const newPoints = { start: {x: p1x, y: p1y}, end: {x: p2x, y: p2y} };
          if (isOpposite) {
              prevOppositeGapPoints = newPoints;
          } else {
              prevNormalGapPoints = newPoints;
          }
      } else {
          prevNormalGapPoints = null;
          prevOppositeGapPoints = null;
      }
  }
  ctx.globalAlpha = 1.0;
};

export const drawBall = (
  ctx: CanvasRenderingContext2D,
  b: Ball,
  config: SimulationConfig,
  rgb: string
) => {
  drawAdvancedBall(ctx, b, config, rgb);
};
